// src/phase4/entities/BaseSprite.ts
// Base platform sprite - preset locations where towers can be placed

import Phaser from 'phaser';

export interface BaseSpriteConfig {
  id: string;
  x: number;
  y: number;
  allowedTowerTypes?: ('CPU_MAIN' | 'LANGUAGE_SIDE' | 'NETWORK_SIDE')[];
  isOccupied?: boolean;
  scene: Phaser.Scene;
}

export class BaseSprite extends Phaser.GameObjects.Container {
  private config: BaseSpriteConfig;

  // Visual elements
  private baseGraphics: Phaser.GameObjects.Graphics;
  private ringGraphics: Phaser.GameObjects.Graphics;

  // State
  private _isOccupied: boolean = false;

  constructor(scene: Phaser.Scene, config: BaseSpriteConfig) {
    super(scene, config.x, config.y);
    this.config = config;
    this._isOccupied = config.isOccupied || false;

    console.log('[BaseSprite] Constructor called for:', config.id);

    this.baseGraphics = scene.add.graphics();
    this.baseGraphics.setName('baseGraphics');
    this.ringGraphics = scene.add.graphics();
    this.ringGraphics.setName('ringGraphics');

    this.init();
  }

  private init(): void {
    console.log('[BaseSprite] init() called for:', this.config.id);
    this.createBase();
    this.createRing();
    this.setupInteraction();
    console.log('[BaseSprite] init() complete for:', this.config.id, 'input enabled:', this.input?.enabled);
  }

  private createBase(): void {
    // Base platform - neutral gray color
    this.baseGraphics.clear();
    this.baseGraphics.fillStyle(0x3a362e, 1);
    this.baseGraphics.fillCircle(0, 0, 28);
    this.baseGraphics.lineStyle(3, 0x4a473f, 1);
    this.baseGraphics.strokeCircle(0, 0, 28);
    this.add(this.baseGraphics);

    // Inner ring
    this.baseGraphics.lineStyle(1, 0x6b6459, 0.5);
    this.baseGraphics.strokeCircle(0, 0, 20);
  }

  private createRing(): void {
    // Pulsing ring indicator for empty bases
    this.ringGraphics.clear();
    if (!this._isOccupied) {
      console.log('[BaseSprite] Creating pulsing ring for base:', this.config.id);
      this.ringGraphics.lineStyle(2, 0xe6a640, 0.6);
      this.ringGraphics.strokeCircle(0, 0, 36);

      // Add subtle pulse animation
      this.scene.tweens.add({
        targets: this.ringGraphics,
        alpha: 0.3,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Quad.easeInOut',
      });
    }
    this.add(this.ringGraphics);
  }

  private setupInteraction(): void {
    console.log('[BaseSprite] setupInteraction() called for:', this.config.id);

    // Use Container's native interactive with circle hit area
    // The hit area is relative to Container's local (0,0), which is where graphics are drawn
    this.setInteractive(new Phaser.Geom.Circle(0, 0, 32), Phaser.Geom.Circle.Contains);

    // Listen for pointerdown on this Container
    this.on('pointerdown', (_pointer: Phaser.Input.Pointer, localX: number, localY: number) => {
      if (this._isOccupied) return;

      // Check if click is within the circle hit area
      const hitCircle = new Phaser.Geom.Circle(0, 0, 32);
      if (Phaser.Geom.Circle.Contains(hitCircle, localX, localY)) {
        console.log('[BaseSprite] Click detected on base:', this.config.id);
        const eventData = {
          baseId: this.config.id,
          x: this.x,
          y: this.y,
          allowedTypes: this.config.allowedTowerTypes,
        };
        console.log('[BaseSprite] Emitting baseSelect event:', eventData);
        this.scene.events.emit('baseSelect', eventData);
      }
    });

    console.log('[BaseSprite] Interaction set up, input enabled:', this.input?.enabled);
  }

  private unhighlight(): void {
    this.baseGraphics.clear();
    this.baseGraphics.fillStyle(0x3a362e, 1);
    this.baseGraphics.fillCircle(0, 0, 28);
    this.baseGraphics.lineStyle(3, 0x4a473f, 1);
    this.baseGraphics.strokeCircle(0, 0, 28);
  }

  // Public API
  get isOccupied(): boolean {
    return this._isOccupied;
  }

  occupy(): void {
    this._isOccupied = true;
    this.ringGraphics.clear();
    this.unhighlight();
    this.disableInteractive();
  }

  vacate(): void {
    this._isOccupied = false;
    this.createRing();
    this.setInteractive(new Phaser.Geom.Circle(0, 0, 32), Phaser.Geom.Circle.Contains);
  }

  getId(): string {
    return this.config.id;
  }

  getX(): number {
    return this.x;
  }

  getY(): number {
    return this.y;
  }

  destroy(): void {
    this.scene.tweens.killTweensOf(this.ringGraphics);
    super.destroy();
  }
}
